// Contrôle de MISE EN PAGE d'une animation : ce que voit l'élève à chaque
// instant clé de chaque scène (après chaque étape, à chaque phrase, à chaque
// question). Pur : la validation (Studio et serveur) l'appelle sur le
// document compilé.
//
// Détecte ce qu'un œil humain refuse et qu'une IA ne voit pas :
//   - deux textes (ou formules, nombres) qui se chevauchent ;
//   - un texte qui touche ou dépasse le cadre, le cercle, l'ellipse qui
//     l'entoure, ou une forme voisine ;
//   - un trait (segment, flèche, côté de polygone) qui coupe un texte ;
//   - un texte qui sort du canevas ;
//   - les sous-titres qui cachent un objet ;
//   - le panneau « À toi » (question, outil) qui cache le contexte.
//
// Les textes sont MESURÉS (`measure.ts`) : vraie largeur à quelques % près.

import { JRAF_LIMITS as L } from './limits';
import {
  FORMULA_PAD_Y,
  measureFormula,
  measureText,
  TEXT_LINE_HEIGHT,
  type Extent,
} from './measure';
import { splitSentences } from './narration';
import type { JrafDocument } from './schema/document';
import type { JrafObject } from './schema/objects';
import type { JrafStep } from './schema/steps';
import { stepTargets } from './schema/steps';
import {
  sceneStateAt,
  type CompiledAnimation,
  type CompiledScene,
  type ObjectState,
  type Point,
  type ResolvedObject,
} from './timeline';

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

// ------------------------------------------------- géométrie de l'interface

/** Sous-titres : `inset-x-4 bottom-6`, `px-3 py-2`, 17 px, interligne 1,375. */
export const CAPTION_GEOMETRY = {
  side: 16,
  bottom: 24,
  padX: 12,
  padY: 8,
  size: 17,
  lineHeight: 1.375,
} as const;

/** Panneau « À toi » : `inset-x-3 bottom-3 p-4 gap-3`, haut de 50 % au plus. */
export const PANEL_GEOMETRY = {
  side: 12,
  bottom: 12,
  pad: 16,
  gap: 12,
  maxHeightRatio: 0.5,
  promptSize: 18,
  /** place du bouton « Voir la scène », à droite de la question */
  promptInsetRight: 40,
  choiceSize: 17,
  choiceMinHeight: 48,
  choiceGap: 8,
  lineHeight: 1.375,
  /** bouton : px-3 py-2, bordure 2, icône de réponse */
  choiceChrome: { x: 12 + 2 + 28, y: 8 + 2 },
  /** bouton « Continuer » et texte d'explication après la réponse */
  toolButtons: 2,
  /** dans la vidéo, des choix plus serrés (pas de doigt) : `min-h-[40px] py-1.5 gap-1.5` */
  video: { choiceMinHeight: 40, choiceGap: 6, choiceChromeY: 6 + 2 },
} as const;

/**
 * Hauteur du texte d'un sous-titre : ses lignes, plus ce qu'une formule haute
 * (fraction) ajoute à la sienne — comme dans le lecteur, où KaTeX agrandit la ligne.
 */
export function captionTextHeight(m: Extent): number {
  const g = CAPTION_GEOMETRY;
  return m.lines * g.size * g.lineHeight + mathExtra(m, g.size);
}

/**
 * Ce qu'une formule haute (fraction) ajoute aux lignes d'un texte mesuré. Mesuré
 * dans le lecteur (sous-titres et panneau « À toi », Chromium) : la ligne grandit
 * d'environ 70 % de ce qu'ajoute la mesure du canevas (interligne plus serré).
 */
function mathExtra(m: Extent, size: number): number {
  return Math.max(0, m.height - m.lines * TEXT_LINE_HEIGHT * size) * 0.7;
}

/** Rectangle des sous-titres d'une phrase (repère du canevas). */
export function captionRect(text: string, canvas: { width: number; height: number }): Rect {
  const g = CAPTION_GEOMETRY;
  const maxText = canvas.width - 2 * g.side - 2 * g.padX;
  const m = measureText(text, g.size, 'normal', maxText);
  const width = Math.min(m.inkWidth, maxText) + 2 * g.padX;
  const height = captionTextHeight(m) + 2 * g.padY;
  const y1 = canvas.height - g.bottom;
  return {
    x0: canvas.width / 2 - width / 2,
    x1: canvas.width / 2 + width / 2,
    y0: y1 - height,
    y1,
  };
}

/** Hauteur estimée du panneau d'une étape interactive (avant la réponse). */
export function panelHeight(step: JrafStep, canvas: { width: number; height: number }): number {
  return Math.min(panelContentHeight(step, canvas), canvas.height * PANEL_GEOMETRY.maxHeightRatio);
}

/**
 * Hauteur du contenu du panneau, sans le plafond de la moitié de l'écran : au-delà,
 * l'app fait défiler le panneau, la vidéo coupe le bas (les derniers choix).
 */
export function panelContentHeight(
  step: JrafStep,
  canvas: { width: number; height: number },
  opts: { video?: boolean } = {},
): number {
  const g = PANEL_GEOMETRY;
  const inner = canvas.width - 2 * g.side - 2 * g.pad;
  const choiceMin = opts.video ? g.video.choiceMinHeight : g.choiceMinHeight;
  const choiceGap = opts.video ? g.video.choiceGap : g.choiceGap;
  const chromeY = opts.video ? g.video.choiceChromeY : g.choiceChrome.y;
  if (step.action === 'question.ask') {
    const prompt = measureText(step.prompt, g.promptSize, 'bold', inner - g.promptInsetRight);
    let h =
      2 * g.pad +
      prompt.lines * g.promptSize * g.lineHeight +
      mathExtra(prompt, g.promptSize) +
      g.gap;
    step.choices.forEach((c, i) => {
      const m = measureText(c, g.choiceSize, 'normal', inner - g.choiceChrome.x);
      h += Math.max(
        choiceMin,
        m.lines * g.choiceSize * 1.5 + mathExtra(m, g.choiceSize) + 2 * chromeY,
      );
      if (i > 0) h += choiceGap;
    });
    return h;
  }
  if (step.action === 'tool.open') {
    const prompt = measureText(step.prompt, g.promptSize, 'bold', inner - 28 - g.promptInsetRight);
    return (
      2 * g.pad +
      prompt.lines * g.promptSize * g.lineHeight +
      mathExtra(prompt, g.promptSize) +
      g.toolButtons * (g.choiceMinHeight + g.gap)
    );
  }
  return 0;
}

/** Rectangle du panneau « À toi » d'une étape interactive. */
export function panelRect(step: JrafStep, canvas: { width: number; height: number }): Rect {
  const g = PANEL_GEOMETRY;
  const y1 = canvas.height - g.bottom;
  return { x0: g.side, x1: canvas.width - g.side, y0: y1 - panelHeight(step, canvas), y1 };
}

// ------------------------------------------------------- objets à l'écran

export type LayoutShape =
  | { kind: 'rect'; rect: Rect; corners: Point[]; rotated: boolean }
  | { kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number }
  | { kind: 'polygon'; points: Point[] };

export interface LayoutItem {
  id: string;
  type: JrafObject['type'];
  /**
   * ce que l'élève voit en plus de l'objet : le nom d'un point (« N »), le
   * geste d'une formule qui se transforme (« Remplacer »)
   */
  role?: 'point-label' | 'gesture';
  /** son texte (nom du point, geste) */
  caption?: string;
  /** texte, formule, nombre : boîte d'encre (ou du cadre `box`) */
  text?: { ink: Rect; framed: boolean; size: number };
  /** forme fermée (cadre, cercle, ellipse, polygone) */
  shape?: LayoutShape;
  /** traits : segments, flèches, côtés de polygone, contour d'un rectangle tourné */
  segments?: { a: Point; b: Point; width: number }[];
  /** rectangle englobant (repère du canevas) */
  bounds: Rect;
  /** forme remplie (un fond) : son intérieur compte, pas seulement son contour */
  filled?: boolean;
}

const VISIBLE = 0.15;

function rect(x0: number, y0: number, x1: number, y1: number): Rect {
  return { x0: Math.min(x0, x1), y0: Math.min(y0, y1), x1: Math.max(x0, x1), y1: Math.max(y0, y1) };
}

function boundsOf(points: Point[]): Rect {
  return rect(
    Math.min(...points.map((p) => p.x)),
    Math.min(...points.map((p) => p.y)),
    Math.max(...points.map((p) => p.x)),
    Math.max(...points.map((p) => p.y)),
  );
}

/** Transformation d'un objet (même ordre que le rendu : échelle, rotation, translation). */
type Xf = (p: Point) => Point;

function stateTransform(s: ObjectState): { xf: Xf; scale: number; rotated: boolean } {
  const k = s.scale * s.pop;
  const rad = (s.rotate * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const so = s.scaleOrigin;
  const ro = s.rotateOrigin;
  const xf: Xf = (p) => {
    const x1 = so.x + (p.x - so.x) * k;
    const y1 = so.y + (p.y - so.y) * k;
    const x2 = ro.x + (x1 - ro.x) * cos - (y1 - ro.y) * sin;
    const y2 = ro.y + (x1 - ro.x) * sin + (y1 - ro.y) * cos;
    return { x: x2 + s.dx, y: y2 + s.dy };
  };
  return { xf, scale: k, rotated: Math.abs(s.rotate % 360) > 0.5 };
}

const measureCache = new Map<string, Extent>();
function cached(key: string, f: () => Extent): Extent {
  let e = measureCache.get(key);
  if (!e) {
    if (measureCache.size > 2000) measureCache.clear();
    e = f();
    measureCache.set(key, e);
  }
  return e;
}

const valueFormats = new Map<number, Intl.NumberFormat>();
/** Nombre affiché en écriture française (comme le lecteur). */
export function formatFrenchNumber(v: number, decimals: number): string {
  let f = valueFormats.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    valueFormats.set(decimals, f);
  }
  return f.format(v);
}

/** Boîte d'encre d'un texte à sa place (avant transformation), et sa taille. */
function textInk(
  o: ResolvedObject,
  s: ObjectState,
): { ink: Rect; framed: boolean; size: number } | null {
  if (o.type !== 'text' && o.type !== 'formula' && o.type !== 'value') return null;
  let e: Extent;
  if (o.type === 'text') {
    const label = s.label ?? o.text;
    e = cached(`t|${o.size}|${o.weight}|${o.maxWidth ?? ''}|${label}`, () =>
      measureText(label, o.size, o.weight, o.maxWidth),
    );
  } else if (o.type === 'formula') {
    const tex = s.tex ?? o.tex;
    e = cached(`f|${o.size}|${tex}`, () => measureFormula(tex, o.size));
  } else {
    const v = s.value ?? o.expr;
    const text = `${o.prefix ?? ''}${formatFrenchNumber(v, o.decimals)}${o.suffix ?? ''}`;
    e = cached(`v|${o.size}|${text}`, () => measureText(text, o.size, 'bold'));
  }
  const { x, y } = o.at;
  // boîte de mise en page (ancrée), puis encre dedans
  const bx0 = o.align === 'start' ? x : o.align === 'end' ? x - e.width : x - e.width / 2;
  const bx1 = bx0 + e.width;
  let ix0: number;
  if (o.type === 'text' && o.maxWidth !== undefined) {
    ix0 = o.align === 'start' ? bx0 : o.align === 'end' ? bx1 - e.inkWidth : x - e.inkWidth / 2;
  } else {
    ix0 = (bx0 + bx1) / 2 - e.inkWidth / 2;
  }
  const cy = y + e.inkOffsetY;
  let ink = rect(ix0, cy - e.inkHeight / 2, ix0 + e.inkWidth, cy + e.inkHeight / 2);
  const box = o.box;
  if (box) {
    // le cadre entoure le contenu (largeur du texte × hauteur des lignes) ; un
    // texte coupé sur plusieurs lignes occupe toute sa largeur max (CSS)
    // épaisseur courante (object.style peut la changer)
    const m = box.padding + (s.strokeWidth ?? box.strokeWidth);
    const contentH = o.type === 'formula' ? e.height - 2 * FORMULA_PAD_Y : e.height;
    const wrapped = o.type === 'text' && o.maxWidth !== undefined && e.lines > 1;
    const cw = wrapped ? e.width : e.inkWidth;
    const cx0 = wrapped ? bx0 : ix0;
    ink = rect(cx0 - m, y - contentH / 2 - m, cx0 + cw + m, y + contentH / 2 + m);
  }
  return { ink, framed: Boolean(box), size: o.size };
}

/** Place d'un objet à l'instant : opacité et transformation (groupes compris). */
export interface ObjectFrame {
  opacity: number;
  xf: (p: Point) => Point;
  scale: number;
  rotated: boolean;
}

/** Opacité et transformation de chaque objet (hors groupes) dans l'état donné. */
export function objectFrames(
  anim: CompiledAnimation,
  state: Record<string, ObjectState>,
): Map<string, ObjectFrame> {
  const byId = new Map(anim.objects.map((o) => [o.id, o]));
  const out = new Map<string, ObjectFrame>();
  for (const o of anim.objects) {
    if (o.type === 'group' || !state[o.id]) continue;
    let opacity = 1;
    const xfs: Xf[] = [];
    let scale = 1;
    let rotated = false;
    let cur: ResolvedObject | undefined = o;
    let guard = 0;
    while (cur && guard++ <= L.maxGroupDepth + 1) {
      const s = state[cur.id];
      if (s) {
        opacity *= s.opacity * s.styleOpacity;
        const x = stateTransform(s);
        xfs.push(x.xf);
        scale *= x.scale;
        rotated ||= x.rotated;
      }
      const parent: ResolvedObject | undefined =
        cur.parent !== undefined ? byId.get(cur.parent) : undefined;
      if (parent?.type === 'group') {
        const gx = parent.x;
        const gy = parent.y;
        xfs.push((p) => ({ x: p.x + gx, y: p.y + gy }));
      }
      cur = parent;
    }
    out.set(o.id, { opacity, xf: (p) => xfs.reduce((q, f) => f(q), p), scale, rotated });
  }
  return out;
}

/** Décalage du nom d'un point (comme le lecteur : `LABEL_OFFSETS` de stage.tsx). */
const POINT_LABEL_OFFSETS: Record<string, { x: number; y: number; ax: number; ay: number }> = {
  n: { x: 0, y: -12, ax: -0.5, ay: -1 },
  ne: { x: 8, y: -8, ax: 0, ay: -1 },
  e: { x: 11, y: 0, ax: 0, ay: -0.5 },
  se: { x: 8, y: 8, ax: 0, ay: 0 },
  s: { x: 0, y: 12, ax: -0.5, ay: 0 },
  sw: { x: -8, y: 8, ax: -1, ay: 0 },
  w: { x: -11, y: 0, ax: -1, ay: -0.5 },
  nw: { x: -8, y: -8, ax: -1, ay: -1 },
};
/** Nom d'un point : 17 px, demi-gras italique, interligne 1. */
export const POINT_LABEL_SIZE = 17;

/** Boîte d'encre du nom d'un point (lettre capitale : ~70 % de la ligne). */
function pointLabelInk(o: Extract<ResolvedObject, { type: 'point' }>, label: string): Rect {
  const off = POINT_LABEL_OFFSETS[o.labelPosition] ?? POINT_LABEL_OFFSETS.ne!;
  const e = cached(`p|${label}`, () => measureText(label, POINT_LABEL_SIZE, 'bold'));
  const w = e.width;
  const h = POINT_LABEL_SIZE;
  const x0 = o.x + off.x + off.ax * w;
  const y0 = o.y + off.y + off.ay * h;
  return rect(x0, y0 + h * 0.15, x0 + w, y0 + h * 0.85);
}

/** Pastille du geste d'une formule qui se transforme : 28 px au-dessus, 13 px. */
function gestureInk(
  o: Extract<ResolvedObject, { type: 'formula' }>,
  tex: string,
  gesture: string,
): Rect {
  const f = cached(`f|${o.size}|${tex}`, () => measureFormula(tex, o.size));
  const g = cached(`g|${gesture}`, () => measureText(gesture, 13, 'bold'));
  const w = g.width + 20;
  const cx =
    o.align === 'start' ? o.at.x + f.width / 2 : o.align === 'end' ? o.at.x - f.width / 2 : o.at.x;
  const top = o.at.y - f.height / 2;
  return rect(cx - w / 2, top - 28, cx + w / 2, top - 8);
}

/** Objets visibles d'une scène à l'instant `t` (étapes commencées : finies). */
export function sceneLayout(
  anim: CompiledAnimation,
  scene: CompiledScene,
  t: number,
): LayoutItem[] {
  const state = sceneStateAt(scene, t, { reducedMotion: true });
  const frames = objectFrames(anim, state);
  const out: LayoutItem[] = [];

  for (const o of anim.objects) {
    if (o.type === 'group') continue;
    const s = state[o.id];
    const c = frames.get(o.id);
    if (!s || !c) continue;
    if (c.opacity < VISIBLE) continue;
    const xf = c.xf;

    // ce que le lecteur dessine en plus : nom d'un point, geste d'une formule
    if (o.type === 'point' && !c.rotated) {
      const label = s.label ?? o.label;
      if (label) {
        const r0 = pointLabelInk(o, label);
        const a = xf({ x: r0.x0, y: r0.y0 });
        const b = xf({ x: r0.x1, y: r0.y1 });
        const ink = rect(a.x, a.y, b.x, b.y);
        out.push({
          id: o.id,
          type: o.type,
          role: 'point-label',
          caption: label,
          text: { ink, framed: false, size: POINT_LABEL_SIZE * c.scale },
          bounds: ink,
        });
      }
      continue;
    }
    if (o.type === 'formula' && s.gesture && !c.rotated) {
      const r0 = gestureInk(o, s.tex ?? o.tex, s.gesture);
      const a = xf({ x: r0.x0, y: r0.y0 });
      const b = xf({ x: r0.x1, y: r0.y1 });
      const ink = rect(a.x, a.y, b.x, b.y);
      out.push({
        id: o.id,
        type: o.type,
        role: 'gesture',
        caption: s.gesture,
        text: { ink, framed: true, size: 13 },
        bounds: ink,
      });
    }

    const t0 = textInk(o, s);
    if (t0) {
      if (c.rotated) continue; // texte tourné : pas contrôlé
      const a = xf({ x: t0.ink.x0, y: t0.ink.y0 });
      const b = xf({ x: t0.ink.x1, y: t0.ink.y1 });
      const ink = rect(a.x, a.y, b.x, b.y);
      out.push({
        id: o.id,
        type: o.type,
        text: { ...t0, ink, size: t0.size * c.scale },
        bounds: ink,
      });
      continue;
    }
    const strokeW = s.strokeWidth ?? 2;
    const filled = s.fill !== null && s.fill !== 'none';
    switch (o.type) {
      case 'rect': {
        const pts = [
          { x: o.x, y: o.y },
          { x: o.x + o.width, y: o.y },
          { x: o.x + o.width, y: o.y + o.height },
          { x: o.x, y: o.y + o.height },
        ].map(xf);
        const bounds = boundsOf(pts);
        out.push({
          id: o.id,
          type: o.type,
          filled,
          shape: { kind: 'rect', rect: bounds, corners: pts, rotated: c.rotated },
          segments: c.rotated ? edges(pts, strokeW, true) : undefined,
          bounds,
        });
        break;
      }
      case 'circle':
      case 'ellipse': {
        if (c.rotated && o.type === 'ellipse') break; // ellipse tournée : pas contrôlée
        const rx = (o.type === 'circle' ? o.r : o.rx) * c.scale;
        const ry = (o.type === 'circle' ? o.r : o.ry) * c.scale;
        const center = xf({ x: o.cx, y: o.cy });
        out.push({
          id: o.id,
          type: o.type,
          filled,
          shape: { kind: 'ellipse', cx: center.x, cy: center.y, rx, ry },
          bounds: rect(center.x - rx, center.y - ry, center.x + rx, center.y + ry),
        });
        break;
      }
      case 'polygon': {
        const pts = o.points.map(xf);
        out.push({
          id: o.id,
          type: o.type,
          filled,
          shape: { kind: 'polygon', points: pts },
          segments: edges(pts, strokeW, true),
          bounds: boundsOf(pts),
        });
        break;
      }
      case 'line':
      case 'arrow': {
        const a = xf({ x: o.x1, y: o.y1 });
        const b = xf({ x: o.x2, y: o.y2 });
        out.push({
          id: o.id,
          type: o.type,
          segments: [{ a, b, width: strokeW + (o.type === 'arrow' ? 6 : 0) }],
          bounds: rect(a.x, a.y, b.x, b.y),
        });
        break;
      }
      default:
        break;
    }
  }
  return out;
}

function edges(points: Point[], width: number, closed: boolean) {
  const out: { a: Point; b: Point; width: number }[] = [];
  for (let i = 0; i < points.length - (closed ? 0 : 1); i++)
    out.push({ a: points[i]!, b: points[(i + 1) % points.length]!, width });
  return out;
}

// ----------------------------------------------------------- géométrie

function intersects(a: Rect, b: Rect, margin = 0): boolean {
  return (
    a.x0 < b.x1 + margin && b.x0 < a.x1 + margin && a.y0 < b.y1 + margin && b.y0 < a.y1 + margin
  );
}

function inset(r: Rect, m: number): Rect {
  return { x0: r.x0 + m, y0: r.y0 + m, x1: r.x1 - m, y1: r.y1 - m };
}

function center(r: Rect): Point {
  return { x: (r.x0 + r.x1) / 2, y: (r.y0 + r.y1) / 2 };
}

function corners(r: Rect): Point[] {
  return [
    { x: r.x0, y: r.y0 },
    { x: r.x1, y: r.y0 },
    { x: r.x1, y: r.y1 },
    { x: r.x0, y: r.y1 },
  ];
}

function insidePolygon(p: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x)
      inside = !inside;
  }
  return inside;
}

/** Le point est-il dans la forme (réduite de `m` vers l'intérieur) ? */
function insideShape(p: Point, s: LayoutShape, m = 0): boolean {
  switch (s.kind) {
    case 'rect': {
      if (!s.rotated) {
        const r = inset(s.rect, m);
        return p.x >= r.x0 && p.x <= r.x1 && p.y >= r.y0 && p.y <= r.y1;
      }
      return insidePolygon(p, s.corners);
    }
    case 'ellipse': {
      const rx = s.rx - m;
      const ry = s.ry - m;
      if (rx <= 0 || ry <= 0) return false;
      return ((p.x - s.cx) / rx) ** 2 + ((p.y - s.cy) / ry) ** 2 <= 1;
    }
    case 'polygon':
      return insidePolygon(p, s.points);
  }
}

/** Points du bord d'un rectangle, tous les `step` px. */
function perimeter(r: Rect, step = 4): Point[] {
  const out: Point[] = [];
  const w = r.x1 - r.x0;
  const h = r.y1 - r.y0;
  const nx = Math.max(1, Math.ceil(w / step));
  const ny = Math.max(1, Math.ceil(h / step));
  for (let i = 0; i <= nx; i++) {
    const x = r.x0 + (w * i) / nx;
    out.push({ x, y: r.y0 }, { x, y: r.y1 });
  }
  for (let j = 1; j < ny; j++) {
    const y = r.y0 + (h * j) / ny;
    out.push({ x: r.x0, y }, { x: r.x1, y });
  }
  return out;
}

/** Distance d'un point à un segment. */
export function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function segmentHitsRect(a: Point, b: Point, r: Rect, pad: number): boolean {
  const g = { x0: r.x0 - pad, y0: r.y0 - pad, x1: r.x1 + pad, y1: r.y1 + pad };
  // Liang-Barsky
  let t0 = 0;
  let t1 = 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const clip = (p: number, q: number) => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  return (
    clip(-dx, a.x - g.x0) && clip(dx, g.x1 - a.x) && clip(-dy, a.y - g.y0) && clip(dy, g.y1 - a.y)
  );
}

/**
 * L'objet — sa forme, pas son rectangle englobant — touche-t-il le rectangle
 * (sous-titres, panneau) ? Une forme vide ne compte que par son contour.
 */
export function itemHitsRect(it: LayoutItem, r: Rect): boolean {
  if (it.text) return intersects(it.text.ink, r, -1);
  if (!intersects(it.bounds, r, 1)) return false;
  const inner = inset(r, 1);
  if (it.segments?.some((sg) => segmentHitsRect(sg.a, sg.b, inner, sg.width / 2))) return true;
  const sh = it.shape;
  if (!sh) return false;
  const pts = corners(inner);
  switch (sh.kind) {
    case 'rect': {
      if (sh.rotated) return Boolean(it.filled) && pts.some((p) => insidePolygon(p, sh.corners));
      if (it.filled) return true;
      const c = sh.corners;
      return [0, 1, 2, 3].some((i) => segmentHitsRect(c[i]!, c[(i + 1) % 4]!, inner, 1));
    }
    case 'ellipse': {
      // point du rectangle le plus proche du centre : dans l'ellipse ?
      const px = Math.max(inner.x0, Math.min(sh.cx, inner.x1));
      const py = Math.max(inner.y0, Math.min(sh.cy, inner.y1));
      if (!insideShape({ x: px, y: py }, sh)) return false;
      if (it.filled) return true;
      // vide : le contour passe dans le rectangle sauf s'il est tout entier dedans
      return !pts.every((p) => insideShape(p, sh, 2));
    }
    case 'polygon':
      return Boolean(it.filled) && pts.some((p) => insidePolygon(p, sh.points));
  }
}

// ------------------------------------------------------------- contrôle

export type LayoutIssueCode =
  | 'layout_overlap'
  | 'layout_frame_tight'
  | 'layout_touch'
  | 'layout_crossed'
  | 'layout_outside'
  | 'layout_captions'
  | 'layout_under_panel'
  /** conseil : la question déborde la moitié de l'écran (coupée dans la vidéo) */
  | 'layout_panel_overflow';

export interface LayoutIssue {
  code: LayoutIssueCode;
  /** objet en cause (son chemin dans le document) */
  objectId: string;
  sceneIndex: number;
  message: string;
}

/** Marge minimale entre un texte et le bord d'un cadre qui l'entoure (px). */
export const FRAME_MARGIN = 6;
/** Écart minimal entre deux textes (px) : en dessous, ils « se touchent ». */
export const TEXT_GAP = 2;

/** Instants contrôlés d'une scène : début, chaque étape, chaque phrase, chaque question. */
export function sampleTimes(scene: CompiledScene): number[] {
  const ts = new Set<number>([0]);
  for (const st of scene.schedule.steps) ts.add(st.startMs);
  for (const s of scene.sentenceStartsMs) ts.add(s);
  ts.add(scene.schedule.endMs);
  return [...ts].sort((a, b) => a - b);
}

function px(n: number): string {
  return String(Math.round(n));
}

/** Nom d'un élément dans un message (le nom d'un point, le geste d'une formule…). */
function nameOf(it: LayoutItem): string {
  if (it.role === 'point-label') return `le nom du point ${it.caption} (« ${it.id} »)`;
  if (it.role === 'gesture') return `le geste « ${it.caption} » de « ${it.id} »`;
  return `« ${it.id} »`;
}

/** Idem, « le texte « id » » pour un texte, une formule, un nombre. */
function textName(it: LayoutItem): string {
  return it.role ? nameOf(it) : `le texte « ${it.id} »`;
}

function span(r: Rect): string {
  return `y ${px(r.y0)}–${px(r.y1)}, x ${px(r.x0)}–${px(r.x1)}`;
}

/** Écart minimal conseillé entre deux textes (px). */
const SUGGESTED_GAP = 12;

/** Contrôle de mise en page de toute l'animation (document validé, compilé). */
export function checkLayout(anim: CompiledAnimation): LayoutIssue[] {
  const doc: JrafDocument = anim.doc;
  const W = doc.canvas.width;
  const H = doc.canvas.height;
  const issues: LayoutIssue[] = [];
  const seen = new Set<string>();
  const report = (key: string, issue: LayoutIssue) => {
    if (seen.has(key)) return;
    seen.add(key);
    issues.push(issue);
  };
  const anchorY = new Map<string, number>();
  for (const o of anim.objects)
    if (o.type === 'text' || o.type === 'formula' || o.type === 'value') anchorY.set(o.id, o.at.y);

  anim.scenes.forEach((scene, si) => {
    const title = scene.scene.title;
    const where = `scène « ${title} »`;
    const sentences = splitSentences(scene.scene.narration.text);
    const captions = sentences.map((s) => captionRect(s, doc.canvas));
    const gates = scene.schedule.steps.filter((g) => g.gate);
    const times = sampleTimes(scene);
    // premier instant où chaque élément est visible dans la scène : le dernier montré est à déplacer
    const firstSeen = new Map<string, number>();

    for (const t of times) {
      const items = sceneLayout(anim, scene, t);
      const texts = items.filter((i) => i.text);
      const keyOf = (i: LayoutItem) => `${i.id}|${i.role ?? ''}`;
      for (const it of texts) if (!firstSeen.has(keyOf(it))) firstSeen.set(keyOf(it), t);

      // texte hors du canevas
      for (const it of texts) {
        const r = it.text!.ink;
        if (r.x0 < 0 || r.y0 < 0 || r.x1 > W || r.y1 > H) {
          const width = r.x1 - r.x0;
          report(`out|${keyOf(it)}`, {
            code: 'layout_outside',
            objectId: it.id,
            sceneIndex: si,
            message:
              width > W - 40
                ? `${nameOf(it)} mesure ${px(width)} px de large et sort du canevas (${where}) : texte plus court, plus petit, ou maxWidth avec align start`
                : `${nameOf(it)} dépasse du canevas (${span(r)}, ${where}) : rapproche-le du centre`,
          });
        }
      }

      // textes qui se chevauchent
      for (let i = 0; i < texts.length; i++)
        for (let j = i + 1; j < texts.length; j++) {
          const a = texts[i]!;
          const b = texts[j]!;
          if (a.id === b.id) continue; // une formule et son geste
          if (!intersects(a.text!.ink, b.text!.ink, TEXT_GAP)) continue;
          const [p, q] = [keyOf(a), keyOf(b)].sort();
          // le dernier montré bouge ; à égalité, un texte plutôt que le nom d'un
          // point, puis celui du dessous
          const fa = firstSeen.get(keyOf(a)) ?? 0;
          const fb = firstSeen.get(keyOf(b)) ?? 0;
          const later =
            fa !== fb
              ? fa > fb
                ? a
                : b
              : Boolean(a.role) !== Boolean(b.role)
                ? a.role
                  ? b
                  : a
                : a.text!.ink.y0 > b.text!.ink.y0
                  ? a
                  : b;
          const other = later === a ? b : a;
          const lr = later.text!.ink;
          const or = other.text!.ink;
          let fix: string;
          if (later.role === 'gesture')
            fix = `une formule qui se transforme affiche son geste 28 px au-dessus d'elle : descends « ${later.id} » d'au moins ${px(or.y1 + SUGGESTED_GAP - lr.y0)} px, ou cache « ${other.id} » avant`;
          else if (later.role === 'point-label' || other.role === 'point-label')
            fix = `écarte-les d'au moins ${SUGGESTED_GAP} px (déplace le texte, ou change labelPosition du point)`;
          else {
            const y = anchorY.get(later.id);
            const down = or.y1 + SUGGESTED_GAP - lr.y0;
            const up = lr.y1 - (or.y0 - SUGGESTED_GAP);
            fix =
              y === undefined
                ? `écarte-les d'au moins ${SUGGESTED_GAP} px, ou cache l'un avant de montrer l'autre`
                : `mets « ${later.id} » à y = ${px(y + down)} (dessous) ou y = ${px(y - up)} (dessus), ou cache « ${other.id} » avant de montrer « ${later.id} »`;
          }
          report(`ov|${p}|${q}`, {
            code: 'layout_overlap',
            objectId: later.id,
            sceneIndex: si,
            message: `${nameOf(later)} (${span(lr)}) chevauche ${nameOf(other)} (${span(or)}) (${where}) : ${fix}`,
          });
        }

      // texte et formes : dedans avec une marge, ou franchement dehors
      for (const tx of texts) {
        const ink = tx.text!.ink;
        const mid = center(ink);
        for (const sh of items) {
          if (!sh.shape || sh.id === tx.id) continue;
          if (!intersects(ink, sh.bounds, 1)) continue;
          const containsCenter = insideShape(mid, sh.shape);
          if (containsCenter) {
            const ok = perimeter(ink).every((p) => insideShape(p, sh.shape!, FRAME_MARGIN));
            if (!ok) {
              const w = px(ink.x1 - ink.x0);
              const h = px(ink.y1 - ink.y0);
              report(`fr|${keyOf(tx)}|${sh.id}`, {
                code: 'layout_frame_tight',
                objectId: sh.id,
                sceneIndex: si,
                message:
                  sh.shape.kind === 'rect' && !tx.text!.framed
                    ? `${textName(tx)} (${w} × ${h} px) touche ou dépasse le cadre « ${sh.id} » (${where}) : supprime « ${sh.id} » et mets box sur « ${tx.id} » (le cadre suit le texte)`
                    : `${textName(tx)} (${w} × ${h} px) touche le bord de « ${sh.id} » (${where}) : agrandis « ${sh.id} », réduis le texte ou sors-le de la forme`,
              });
            }
          } else {
            const touches = perimeter(ink).some((p) => insideShape(p, sh.shape!, -2));
            if (touches)
              report(`to|${keyOf(tx)}|${sh.id}`, {
                code: 'layout_touch',
                objectId: tx.id,
                sceneIndex: si,
                message: `${textName(tx)} (${span(ink)}) touche « ${sh.id} » (${where}) : écarte-le d'au moins 8 px`,
              });
          }
        }
        // traits qui coupent le texte
        for (const ln of items) {
          if (!ln.segments || ln.id === tx.id) continue;
          if (ln.shape?.kind === 'polygon' && insideShape(mid, ln.shape)) {
            // texte DANS un polygone : ses côtés comptent comme un cadre
            const ok = perimeter(ink).every((p) =>
              ln.segments!.every((sg) => distToSegment(p, sg.a, sg.b) >= FRAME_MARGIN),
            );
            if (!ok)
              report(`fr|${keyOf(tx)}|${ln.id}`, {
                code: 'layout_frame_tight',
                objectId: ln.id,
                sceneIndex: si,
                message: `${textName(tx)} touche un côté de « ${ln.id} » (${where}) : déplace-le vers l'intérieur ou réduis-le`,
              });
            continue;
          }
          const hit = ln.segments.some((sg) =>
            segmentHitsRect(sg.a, sg.b, inset(ink, 1), sg.width / 2),
          );
          if (hit)
            report(`cr|${keyOf(tx)}|${ln.id}`, {
              code: 'layout_crossed',
              objectId: tx.id,
              sceneIndex: si,
              message: `le trait « ${ln.id} » coupe ${textName(tx)} (${span(ink)}, ${where}) : ${tx.role === 'point-label' ? 'change labelPosition du point (côté libre)' : 'déplace le texte à côté du trait (8 px au moins)'}`,
            });
        }
      }

      // sous-titres (pas pendant une question : ils sont masqués)
      const gatesNow = gates.filter((g) => g.startMs === t);
      if (doc.player.captions && sentences.length && gatesNow.length === 0) {
        let k = 0;
        scene.sentenceStartsMs.forEach((s, i) => {
          if (s <= t) k = i;
        });
        const cap = captions[k];
        if (cap)
          for (const it of items) {
            if (!itemHitsRect(it, cap)) continue;
            report(`cap|${it.id}|${it.role ?? ''}`, {
              code: 'layout_captions',
              objectId: it.id,
              sceneIndex: si,
              message: `les sous-titres cachent ${nameOf(it)} (${where}, phrase ${k + 1}) : rien sous y = ${px(cap.y0 - 8)}`,
            });
          }
      }

      // panneau « À toi » (plusieurs portes au même instant : l'une après l'autre)
      for (const gate of gatesNow) {
        const step = scene.scene.steps[gate.index]!;
        const panel = panelRect(step, doc.canvas);
        // l'app fait défiler un panneau trop haut ; la vidéo, elle, le coupe
        const content = panelContentHeight(step, doc.canvas, { video: true });
        // la marge du bas du panneau (p-4) peut être coupée sans rien cacher ;
        // l'estimation dépasse le rendu d'environ 6 px (mesuré dans Chromium)
        if (content > panel.y1 - panel.y0 + PANEL_GEOMETRY.pad + 6)
          report(`po|${gate.id}`, {
            code: 'layout_panel_overflow',
            objectId: gate.id,
            sceneIndex: si,
            message: `« ${gate.id} » : la question et ses choix font environ ${px(content)} px, plus que la moitié de l'écran (${px(panel.y1 - panel.y0)} px) : dans la vidéo, le bas est coupé ; raccourcis la question ou les choix, ou mets-en moins`,
          });
        const targets = new Set(stepTargets(step));
        for (const it of items) {
          if (targets.has(it.id)) continue;
          if (!itemHitsRect(it, panel)) continue;
          report(`pa|${it.id}|${it.role ?? ''}|${gate.id}`, {
            code: 'layout_under_panel',
            objectId: it.id,
            sceneIndex: si,
            message: `pendant « ${gate.id} », le panneau « À toi » cache ${nameOf(it)} (${where}) : place le contexte de la question au-dessus de y = ${px(panel.y0 - 12)}`,
          });
        }
      }
    }
  });
  return issues;
}
